import React from 'react';
import { injectIntl } from 'react-intl';
import { Button, Icon, Checkbox, Responsive } from 'semantic-ui-react';
import { breakpoints } from 'helpers/breakpoints';
import { DATE_SCHEMA_WITH_TIME, DATE_SCHEMA_WITH_YEAR, DATE_SCHEMA_WITHOUT_YEAR, DATE_SCHEMA_WITHOUT_DATE } from '../../../consts';
import styles from '../../styles-module.css';
import StatusCell from '../status-cell';
import { stripTimezone } from '../../../../../../src/utils';

const ListingRow = injectIntl((props) => {
  const {
    listing,
    listingIds,
    hideCheckBoxes,
    intl,
    updateListingIds,
    key,
    setShowTicketTypes,
    showTicketTypes,
    selectedTicketTypes,
    setSelectedTicketTypes,
  } = props;

  const handleListingCheck = (selectTicketTypes) => {
    const listingIdsClone = Object.assign([], listingIds);
    listingIdsClone.push(listing.id);
    
    if (selectTicketTypes) {
      const allTicketTypeIds = listing?.rates?.nodes?.map(ticketType => ticketType.id);     
      setSelectedTicketTypes({ ...selectedTicketTypes, [listing.id]: new Set(allTicketTypeIds) });
    }
    updateListingIds(listingIdsClone);
  }

  const handleListingUncheck = (deselectTicketTypes) => {
    const newListingIds = listingIds.filter((id) => id !== listing.id);

    if (deselectTicketTypes) {
      const currentSelectedTicketTypes = { ...selectedTicketTypes };
      if (currentSelectedTicketTypes[listing.id]) currentSelectedTicketTypes[listing.id] = undefined; 
      setSelectedTicketTypes(currentSelectedTicketTypes);
    }
    updateListingIds(newListingIds);
  }

  const isSameDay = (startDate, endDate) => startDate.toDateString() === endDate.toDateString();

  const formatTimedEntryDates = (firstTimeslot, lastTimeslot) => {
    const startDate = stripTimezone(firstTimeslot.startAt);
    const endDate = stripTimezone(lastTimeslot.endAt);
       
    if (startDate.getFullYear() === endDate.getFullYear()) {
      return `${intl.formatDate(startDate, DATE_SCHEMA_WITHOUT_YEAR)} -
        ${intl.formatDate(endDate, DATE_SCHEMA_WITH_YEAR)}`;
    }
    return `${intl.formatDate(startDate, DATE_SCHEMA_WITH_YEAR)} - 
      ${intl.formatDate(endDate, DATE_SCHEMA_WITH_YEAR)}`;
  }

  const formatRegularEntryDates = (timeslotCount, firstTimeslot, lastTimeslot) => {
    const startDate = stripTimezone(firstTimeslot.startAt);
    const endDate = stripTimezone(lastTimeslot.endAt);

    if (timeslotCount <= 1) {
      if (isSameDay(startDate, endDate)) {
        return `${intl.formatDate(startDate, DATE_SCHEMA_WITH_TIME)} -
          ${intl.formatDate(endDate, DATE_SCHEMA_WITHOUT_DATE)}`;
      }
      return `${intl.formatDate(startDate, DATE_SCHEMA_WITH_TIME)} -
        ${intl.formatDate(endDate,DATE_SCHEMA_WITH_TIME)}`;
    }
    return `${intl.formatDate(startDate, DATE_SCHEMA_WITH_YEAR)} - 
      ${intl.formatDate(endDate, DATE_SCHEMA_WITH_YEAR)}`;
  }

  const renderDrawerIcon = <Icon color="grey" size="large"
    className={`universe-chevron-${showTicketTypes ? "up" : "down"}`} />;

  const onShowTicketTypes = () => setShowTicketTypes(listing.id, !showTicketTypes);

  const gearIconClassNames = `${styles.settingsButton} ${showTicketTypes ? styles.ticketDrawerActive : null}`;

  const handleTicketTypeSelect = (ticketTypeId, isSelected) => {
    const currentTicketTypes = { ...selectedTicketTypes };
    if (currentTicketTypes[listing.id]) {
      if (isSelected) currentTicketTypes[listing.id].add(ticketTypeId);
      else currentTicketTypes[listing.id].delete(ticketTypeId);
    }
    else currentTicketTypes[listing.id] = new Set([ticketTypeId]);
    
    if (currentTicketTypes[listing.id]?.size === 1 && isSelected) handleListingCheck();
    else if (currentTicketTypes[listing.id]?.size === 0 && !isSelected) handleListingUncheck();
 
    setSelectedTicketTypes(currentTicketTypes);
  }

  const renderTicketTypes = () => {
    const currentSelectedTicketTypes = selectedTicketTypes[listing.id];
    return listing?.rates?.nodes?.map(ticketType => {
      const isTicketTypeSelected = currentSelectedTicketTypes?.has(ticketType.id);
      const ticketTypeContainerClasses =
        `${styles.ticketTypesContainer} ${isTicketTypeSelected ? styles.ticketTypesContainerSelected : ''}`;
      const ticketTypeRowClass = `${styles.ticketTypesCheckbox} ${hideCheckBoxes ? styles.hidden : ''}`;

      const ticketTypeRow = <div className={ticketTypeContainerClasses}>
        <div className={ticketTypeRowClass}>  
          <Checkbox
            checked={isTicketTypeSelected}
            onChange={(_, { checked }) => handleTicketTypeSelect(ticketType.id, checked)}
          />
        </div>
        <div>
          {ticketType.name}
        </div>
      </div>;
      
      if (hideCheckBoxes && !isTicketTypeSelected) return <></>;
      return ticketTypeRow;
    });
  }

  const getEventCheckBoxClasses = () => {
    const allTicketTypesSelected = selectedTicketTypes[listing.id] 
      && (selectedTicketTypes[listing.id]?.size < listing?.rates?.nodes?.length);
    
    return `${styles.checkBoxWrapper} ${styles.title} 
      ${hideCheckBoxes ? styles.hidden : ''}
      ${allTicketTypesSelected ? styles.allTicketTypesSelection : ''}`;
  }

  const desktopListingRowClasses =
    `${styles.desktopListingRow} ${listingIds.includes(listing.id) ? styles.desktopListingRowSelected : ''}`;
  const mobileListingRowClasses =
    `${styles.mobileListingRow} ${listingIds.includes(listing.id) ? styles.mobileListingRowSelected : ''}`;

  return (
    <div key={key}>
      <Responsive minWidth={breakpoints.tablet}>  
        <div className={desktopListingRowClasses}>
          <div className={`${styles.column} ${styles.checkBoxAndImageWrapper}`}>
            <div className={styles.checkBoxAndImage}>
              <div className={getEventCheckBoxClasses()}>
                <Checkbox
                  className={styles.checkbox}
                  checked={listingIds.includes(listing.id)}
                  onChange={(_, data) => data.checked ? handleListingCheck(true) : handleListingUncheck(true)}
                />
              </div>
              <div className={styles.imageWrapper}>
                <img 
                  src={listing.imageUrl}
                  className={styles.image}
                  alt="listing_img" 
                />
              </div>
            </div>
            <div className={`${styles.title}`}>
              {listing.title}
            </div>
          </div>
          <div className={`${styles.column} ${styles.status}`}>
            <StatusCell
              status={listing.state.toUpperCase()}
              intl={intl}
            />
          </div>
          <div className={`${styles.column} ${styles.date}`}>
            {listing.timedEntry
              ? formatTimedEntryDates(listing.firstTimeSlot, listing.lastTimeSlot)  
              : formatRegularEntryDates(listing.timeSlots.nodes.length, listing.firstTimeSlot, listing.lastTimeSlot)
            }
          </div>
          <div className={`${styles.column} ${styles.dropIcon} ${styles.actions}`}>
            <Button
              onClick={onShowTicketTypes}
              className={gearIconClassNames}
              icon={renderDrawerIcon}
              style={{ boxShadow: '0 0 0 0' }}
            />
          </div>
        </div>
        {showTicketTypes && renderTicketTypes()}
        <div className={styles.line} />
      </Responsive>
      <Responsive maxWidth={breakpoints.largestMobile}>
        <div className={mobileListingRowClasses}>
          <div className={`${styles.column}`}>
            <div className={`${styles.column} ${styles.checkBoxAndImageWrapper}`}>
              <div className={styles.checkBoxAndImage}>
                <div className={getEventCheckBoxClasses()}>
                  <Checkbox
                    className={styles.checkbox}
                    checked={listingIds.includes(listing.id)}
                    onChange={(_, data) => {data.checked ? handleListingCheck(true) : handleListingUncheck(true)}}
                  />
                </div>
                <div className={styles.imageWrapper}>
                  <img 
                    src={listing.imageUrl}
                    className={styles.image}
                    alt="listing_img" 
                  />
                </div>
              </div>
              <div className={`${styles.title}`}>
                {listing.title}
              </div>
            </div>
            <div className={`${styles.column} ${styles.date}`}>
              {listing.timedEntry
                ? formatTimedEntryDates(listing.firstTimeSlot, listing.lastTimeSlot)  
                : formatRegularEntryDates(listing.timeSlots.nodes.length, listing.firstTimeSlot, listing.lastTimeSlot)
              }
            </div>
            <div className={`${styles.column} ${styles.status}`}>
              <StatusCell
                status={listing.state.toUpperCase()}
                intl={intl}
              />
            </div>    
          </div>
          <div className={`${styles.column} ${styles.dropIcon} ${styles.actions}`}>
            <Button
              onClick={onShowTicketTypes}
              className={gearIconClassNames}
              icon={renderDrawerIcon}
              style={{ boxShadow: '0 0 0 0' }}
            />
          </div>
        </div>
        {showTicketTypes && renderTicketTypes()}
        <div className={styles.line} />
      </Responsive>
    </div>
  );
});

export default ListingRow;
